import React, { useCallback, useEffect, useRef, useState } from "react";
import { IWebsiteBuilderTable, TWebsiteBuilderTableCell } from "../nodes/container/websiteBuilderTable";
import { IWebsiteBuilderSectionEntityViewBoundingBox } from "../designSpace/views/hooks/sectionEntity/helpers/sectionEntityViewBoundingBox";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { TableBuilderContext } from "./TableBuilderContext";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { flatten } from "lodash";
import { TTableBuilderDomNodeRefMap } from "./hooks/useTableBuilderDomNodeRefMapRegistration";
import { useTableBuilderDragLayerContext } from "./hooks/useTableBuilderDragLayerContext";
import { useTableBuilderOperations } from "./hooks/useTableBuilderOperations";
import { TableBuilderAddDimensionButtons } from "./ui/addDimensionButtons/TableBuilderAddDimensionButtons";
import { TableBuilderRowColorPicker } from "./ui/rowColorPicker/TableBuilderRowColorPicker";
import { TableBuilderTableView } from "./views/TableBuilderTableView";
import { TableBuilderOverlay } from "./overlay/TableBuilderOverlay";
import "./tableBuilder.scss";

interface ITableBuilderProps {
  tableNode: IWebsiteBuilderTable;
  containerBoundingBox: IWebsiteBuilderSectionEntityViewBoundingBox;
  containerBuilderUIPortalId: string;
}

interface ITableBuilderCellSelection {
  type: "cell";
  id: string;
}

type TTableBuilderRowSelectionColorPickerType =
  | "none"
  | "color"
  | "background-color";

interface ITableBuilderRowSelection {
  type: "row";
  id: string;
  colorPickerType: TTableBuilderRowSelectionColorPickerType;
}

interface ITableBuilderColumnSelection {
  type: "column";
  index: number;
}

export type TTableBuilderSelection =
  | ITableBuilderCellSelection
  | ITableBuilderRowSelection
  | ITableBuilderColumnSelection;

export interface ITableBuilderState {
  selection: TTableBuilderSelection;
  hoveredRowId: string;
  hoveredColumnIndex: number;
}

export function TableBuilder(props: ITableBuilderProps) {
  const { tableNode, containerBoundingBox, containerBuilderUIPortalId } = props;

  const dispatch = useAppDispatch();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const tableRef = useRef<HTMLTableElement>(null);
  const keyboardCommandsDisabledRef = useRef(false);
  const domNodeRefMap: TTableBuilderDomNodeRefMap = useRef(new Map());

  const [state, setState] = useState<ITableBuilderState>({
    selection: null,
    hoveredRowId: null,
    hoveredColumnIndex: null,
  });

  const tableNodeSettings = tableNode.container.settings;

  const dragLayerContext = useTableBuilderDragLayerContext({
    containerBoundingBox,
    domNodeRefMap,
    tableNodeId: tableNode.id,
    tableRef,
    tableHeadRow: tableNodeSettings.head.row,
    tableBodyRows: tableNodeSettings.body.rows,
  });

  const operations = useTableBuilderOperations({
    dragLayerContext,
    containerBoundingBox,
    domNodeRefMap,
    tableNode,
    tableRef,
    setState,
  });

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (keyboardCommandsDisabledRef.current) {
        return;
      }

      if (e.key === "Escape") {
        if (state.selection != null) {
          setState(prev => ({ ...prev, selection: null }));
        } else {
          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                type: "container",
                editingMode: {
                  type: "none",
                },
              },
            }),
          );
        }
      } else if (e.key === "Tab") {
        if (state.selection?.type === "cell") {
          e.preventDefault();
          
          const selectedCellId = state.selection.id;

          const headCells = tableNodeSettings.head.row.cells;
          const bodyCells = flatten(tableNodeSettings.body.rows.map(row => row.cells));
          const cells: TWebsiteBuilderTableCell[] = [...headCells, ...bodyCells];

          const selectedCellIndex = cells.findIndex(cell => cell.id === selectedCellId);
          const nextSelectedCellIndex = selectedCellIndex + (e.shiftKey ? -1 : 1);

          if (
            selectedCellIndex === -1 ||
            nextSelectedCellIndex < 0 ||
            nextSelectedCellIndex > cells.length - 1 ||
            (deviceView === "mobile" && nextSelectedCellIndex <= headCells.length - 1)
          ) {
            return;
          }

          const nextSelectedCellId = cells[nextSelectedCellIndex]?.id;

          operations.setSelection({ type: "cell", id: nextSelectedCellId });
        }
      }
    },
    [state.selection, tableNodeSettings.head.row.cells, tableNodeSettings.body.rows, deviceView, operations.setSelection],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  useEffect(() => {
    if (
      state.selection != null &&
      state.selection.type !== "cell" &&
      (
        (
          state.selection.type === "row" &&
          state.selection.id !== state.hoveredRowId &&
          state.selection.colorPickerType === "none"
        ) ||
        (state.selection.type === "column" && state.selection.index !== state.hoveredColumnIndex)
      )
    ) {
      operations.setSelection(null);
    }
  }, [state.selection, state.hoveredRowId, state.hoveredColumnIndex, operations.setSelection]);

  useEffect(() => {
    if (dragLayerContext.type !== "standby") {
      operations.setSelection(null);
    }
  }, [dragLayerContext.type, operations.setSelection]);

  return (
    <TableBuilderContext.Provider
      value={{
        selection: state.selection,
        hoveredRowId: state.hoveredRowId,
        hoveredColumnIndex: state.hoveredColumnIndex,
        dragLayerContext,
        containerBoundingBox,
        containerBuilderUIPortalId,
        domNodeRefMap,
        operations,
        tableNode,
        tableRef,
        keyboardCommandsDisabledRef,
      }}
    >
      <TableBuilderTableView />
      <TableBuilderAddDimensionButtons />
      <TableBuilderOverlay />
      <TableBuilderRowColorPicker />
    </TableBuilderContext.Provider>
  );
}